import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import Integration from "@/model/interface/Integration";
import {
  useGetPodioOrganizationsQuery,
  useGetPodioWorkspacesQuery,
  useGetPodioAppsQuery,
  useUpdateIntegrationSettingsMutation,
  useCreatePodioCallsAppMutation,
  useCreatePodioContactsAppMutation,
  useLinkPodioAppMutation,
} from "@/store/rest_api";
import toast from "react-hot-toast";
import PodioIconPicker from "./PodioIconPicker";

interface Props {
  integration: Integration;
}

export default function PodioSetup({ integration }: Props) {
  const settings = integration.settings || {};
  const initialOrgId = settings.podioOrgId ? Number(settings.podioOrgId) : undefined;
  const initialSpaceId = settings.podioWorkspaceId ? Number(settings.podioWorkspaceId) : undefined;

  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>(initialOrgId);
  const [selectedSpaceId, setSelectedSpaceId] = useState<number | undefined>(initialSpaceId);
  const [callsIconPickerOpen, setCallsIconPickerOpen] = useState(false);
  const [contactsIconPickerOpen, setContactsIconPickerOpen] = useState(false);

  const { data: organizations, isLoading: orgsLoading } = useGetPodioOrganizationsQuery();
  const { data: workspaces, isLoading: workspacesLoading } = useGetPodioWorkspacesQuery(selectedOrgId!, {
    skip: !selectedOrgId,
  });
  const { data: apps, isLoading: appsLoading } = useGetPodioAppsQuery(selectedSpaceId!, {
    skip: !selectedSpaceId,
  });

  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const [createCallsApp, { isLoading: isCreatingCallsApp }] = useCreatePodioCallsAppMutation();
  const [createContactsApp, { isLoading: isCreatingContactsApp }] = useCreatePodioContactsAppMutation();
  const [linkPodioApp] = useLinkPodioAppMutation();

  const contactsAppId = settings.podioContactsAppId ? String(settings.podioContactsAppId) : undefined;
  const callsAppId = settings.podioCallsAppId ? String(settings.podioCallsAppId) : undefined;
  const contactsApp = apps?.find((app) => String(app.appId) === contactsAppId);
  const callsApp = apps?.find((app) => String(app.appId) === callsAppId);
  const contactsAppOptions = apps?.filter((app) => String(app.appId) !== callsAppId) ?? [];
  const callsAppOptions = apps?.filter((app) => String(app.appId) !== contactsAppId) ?? [];

  const saveOrg = async (orgId: number) => {
    setSelectedOrgId(orgId);
    setSelectedSpaceId(undefined);
    try {
      await updateSettings({
        id: integration.id,
        settings: { podioOrgId: orgId, podioWorkspaceId: null, podioContactsAppId: null, podioCallsAppId: null },
      }).unwrap();
    } catch {
      toast.error("Failed to save organization");
    }
  };

  const saveSpace = async (spaceId: number) => {
    setSelectedSpaceId(spaceId);
    try {
      await updateSettings({
        id: integration.id,
        settings: { podioWorkspaceId: spaceId, podioContactsAppId: null, podioCallsAppId: null },
      }).unwrap();
    } catch {
      toast.error("Failed to save workspace");
    }
  };

  const saveApp = async (type: "contacts" | "calls", appId: number) => {
    try {
      await linkPodioApp({ type, appId }).unwrap();
      toast.success("App linked and fields auto-mapped");
    } catch {
      toast.error("Failed to link app");
    }
  };

  const clearApp = async (
    key: "podioContactsAppId" | "podioCallsAppId",
    mappingKey: "podioContactFieldMapping" | "podioCallFieldMapping",
  ) => {
    try {
      await updateSettings({
        id: integration.id,
        settings: { [key]: null, [mappingKey]: null },
      }).unwrap();
    } catch {
      toast.error("Failed to unlink app");
    }
  };

  const handleCreateCallsApp = async (iconId: number) => {
    try {
      await createCallsApp({ iconId }).unwrap();
      toast.success("Calls app created in Podio");
      setCallsIconPickerOpen(false);
    } catch {
      toast.error("Failed to create calls app");
    }
  };

  const handleCreateContactsApp = async (iconId: number) => {
    try {
      await createContactsApp({ iconId }).unwrap();
      toast.success("Contacts app created in Podio");
      setContactsIconPickerOpen(false);
    } catch {
      toast.error("Failed to create contacts app");
    }
  };

  if (integration.status?.toLowerCase() !== "active") {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Connect your Podio account first to configure the workspace.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization & Workspace</CardTitle>
          <CardDescription>Choose which Podio workspace this integration should sync with</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Organization</Label>
            <Select
              value={selectedOrgId ? String(selectedOrgId) : ""}
              onValueChange={(value) => saveOrg(Number(value))}
              disabled={orgsLoading}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder={orgsLoading ? "Loading..." : "Select organization"} />
              </SelectTrigger>
              <SelectContent>
                {organizations?.map((org) => (
                  <SelectItem key={org.orgId} value={String(org.orgId)}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label>Workspace</Label>
            <Select
              value={selectedSpaceId ? String(selectedSpaceId) : ""}
              onValueChange={(value) => saveSpace(Number(value))}
              disabled={!selectedOrgId || workspacesLoading}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder={workspacesLoading ? "Loading..." : "Select workspace"} />
              </SelectTrigger>
              <SelectContent>
                {workspaces?.map((workspace) => (
                  <SelectItem key={workspace.spaceId} value={String(workspace.spaceId)}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contacts App</CardTitle>
          <CardDescription>
            Link an existing Podio app to sync contacts into, or let Retreevo create a pre-configured one
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {contactsAppId ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-green-500/20 p-1">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{contactsApp?.name || `App ${contactsAppId}`}</p>
                  <p className="text-xs text-muted-foreground">Linked</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => clearApp("podioContactsAppId", "podioContactFieldMapping")}>
                Change
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Current</Label>
                  <p className="text-sm text-muted-foreground">Not configured</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setContactsIconPickerOpen(true)}
                  disabled={isCreatingContactsApp || !selectedSpaceId}
                >
                  {isCreatingContactsApp ? "Creating..." : "Create new app"}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <Label>Or pick existing</Label>
                <Select
                  value=""
                  onValueChange={(value) => saveApp("contacts", Number(value))}
                  disabled={!selectedSpaceId || appsLoading || contactsAppOptions.length === 0}
                >
                  <SelectTrigger className="w-72">
                    <SelectValue
                      placeholder={
                        appsLoading
                          ? "Loading..."
                          : contactsAppOptions.length === 0
                          ? "No apps available"
                          : "Select app"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {contactsAppOptions.map((app) => (
                      <SelectItem key={app.appId} value={String(app.appId)}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Calls & Messages App</CardTitle>
          <CardDescription>
            Link an existing Podio app to log calls and SMS into, or let Retreevo create a pre-configured one
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {callsAppId ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-green-500/20 p-1">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{callsApp?.name || `App ${callsAppId}`}</p>
                  <p className="text-xs text-muted-foreground">Linked</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => clearApp("podioCallsAppId", "podioCallFieldMapping")}>
                Change
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Current</Label>
                  <p className="text-sm text-muted-foreground">Not configured</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCallsIconPickerOpen(true)}
                  disabled={isCreatingCallsApp || !selectedSpaceId}
                >
                  {isCreatingCallsApp ? "Creating..." : "Create new app"}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <Label>Or pick existing</Label>
                <Select
                  value=""
                  onValueChange={(value) => saveApp("calls", Number(value))}
                  disabled={!selectedSpaceId || appsLoading || callsAppOptions.length === 0}
                >
                  <SelectTrigger className="w-72">
                    <SelectValue
                      placeholder={
                        appsLoading
                          ? "Loading..."
                          : callsAppOptions.length === 0
                          ? "No apps available"
                          : "Select app"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {callsAppOptions.map((app) => (
                      <SelectItem key={app.appId} value={String(app.appId)}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <PodioIconPicker
        open={contactsIconPickerOpen}
        onOpenChange={setContactsIconPickerOpen}
        title="Pick an icon for the Contacts app"
        description="Browse Podio's icon library or search to narrow down"
        onSelect={handleCreateContactsApp}
        isSubmitting={isCreatingContactsApp}
      />

      <PodioIconPicker
        open={callsIconPickerOpen}
        onOpenChange={setCallsIconPickerOpen}
        title="Pick an icon for the Calls & Messages app"
        description="Browse Podio's icon library or search to narrow down"
        onSelect={handleCreateCallsApp}
        isSubmitting={isCreatingCallsApp}
      />
    </div>
  );
}
